import { submitAttendanceDirect, type SubmitAttendanceRequest } from '@/features/kitchen/api/submitAttendance'
import { dispatchDeliveryToDelmanDirect, type DispatchDeliveryRequest } from '@/features/kitchen/api/dispatchDelivery'
import { saveDispatchDeliveryDirect, type SaveDeliveryRequest } from '@/features/kitchen/api/saveDispatchDelivery'
import { executeWasteBatchDirect, type ExecuteWasteRequest } from '@/features/kitchen/api/executeWaste'
import { postProductionSessionDirect, type PostProductionRequest } from '@/features/kitchen/api/postProduction'
import { submitWasteProposalDirect, type SubmitWasteRequest } from '@/features/kitchen/api/submitWasteProposal'
import { completeTripDirect, type CompleteTripRequest } from '@/features/delman/api/completeTrip'
import { confirmReturnPickupDirect, type ConfirmReturnPickupRequest } from '@/features/delman/api/confirmReturnPickup'
import { generateHandoverQrDirect, type GenerateHandoverQrRequest } from '@/features/delman/api/generateHandoverQr'
import { startTripDirect, type StartTripRequest } from '@/features/delman/api/startTrip'
import {
  submitDelmanAttendanceDirect,
  type SubmitDelmanAttendanceRequest,
} from '@/features/delman/api/submitDelmanAttendance'
import {
  resolveQueuedAttendanceConflict,
  syncDelmanAttendanceFromServer,
} from '@/features/delman/api/delmanAttendanceSync'
import { getDelmanDevice } from '@/shared/auth/delmanDeviceStorage'
import {
  getKitchenSyncQueue,
  removeKitchenSyncItem,
  type KitchenSyncQueueItem,
} from '@/shared/sync/kitchenSyncQueue'

async function processKitchenSyncItem(item: KitchenSyncQueueItem): Promise<void> {
  switch (item.type) {
    case 'production_post':
      await postProductionSessionDirect(item.payload as PostProductionRequest)
      break
    case 'dispatch_save':
      await saveDispatchDeliveryDirect(item.payload as SaveDeliveryRequest)
      break
    case 'dispatch_handover':
      await dispatchDeliveryToDelmanDirect(item.payload as DispatchDeliveryRequest)
      break
    case 'waste_submit':
      await submitWasteProposalDirect(item.payload as SubmitWasteRequest)
      break
    case 'waste_execute':
      await executeWasteBatchDirect(item.payload as ExecuteWasteRequest)
      break
    case 'attendance':
      await submitAttendanceDirect(item.payload as SubmitAttendanceRequest)
      break
    case 'delman_start_trip':
      await startTripDirect(item.payload as StartTripRequest)
      break
    case 'delman_complete_trip':
      await completeTripDirect(item.payload as CompleteTripRequest)
      break
    case 'delman_handover_qr':
      await generateHandoverQrDirect(item.payload as GenerateHandoverQrRequest)
      break
    case 'delman_return_pickup':
      await confirmReturnPickupDirect(item.payload as ConfirmReturnPickupRequest)
      break
    case 'delman_attendance':
      await submitDelmanAttendanceDirect(item.payload as SubmitDelmanAttendanceRequest)
      break
    default:
      break
  }
}

let inFlight: Promise<number> | null = null

// Satu flush pada satu waktu: pemanggil bersamaan (event online, tombol retry,
// halaman) menunggu flush yang sama agar item tidak terkirim dua kali.
export function flushKitchenSyncQueue(): Promise<number> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return Promise.resolve(0)
  if (!inFlight) {
    inFlight = runFlush().finally(() => {
      inFlight = null
    })
  }
  return inFlight
}

async function runFlush(): Promise<number> {
  const queue = getKitchenSyncQueue()
  let flushed = 0
  let touchedDelmanAttendance = false

  for (const item of queue) {
    // Antrean bisa dikosongkan di tengah jalan (mis. perangkat dicabut).
    if (!getKitchenSyncQueue().some((q) => q.id === item.id)) continue
    if (item.type === 'delman_attendance') touchedDelmanAttendance = true
    try {
      await processKitchenSyncItem(item)
      removeKitchenSyncItem(item.id)
      flushed += 1
    } catch (err) {
      // Konflik absensi bersifat final; jangan biarkan memblokir item berikutnya.
      if (item.type === 'delman_attendance' && resolveQueuedAttendanceConflict(item.id, getDelmanDevice(), err)) {
        flushed += 1
        continue
      }
      break
    }
  }

  if (touchedDelmanAttendance) {
    const device = getDelmanDevice()
    if (device) await syncDelmanAttendanceFromServer(device)
  }

  return flushed
}
